import { HTMLAttributes, ReactNode, useState } from "react";
import { useEffect, useRef } from "react";

import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import { SearchInput } from "shared/components/atoms/SearchInput";

import { DropdownArrowIcon } from "@Console/permissions/assets/icons/DropdownArrowIcon";

import {
  InputSearchWithDropdownContainer,
  InputSearchWithDropdownDropdownContainer,
} from "./styles";

interface IInputSearchWithDropdownProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  defaultValueComponent?: ReactNode;
  valueComponent: ReactNode;
  searchInputParams: Parameters<typeof SearchInput>[0];
  required?: boolean;
  shouldCloseOnClick?: boolean;
}

export const InputSearchWithDropdown = ({
  label,
  defaultValueComponent,
  valueComponent,
  searchInputParams,
  required,
  shouldCloseOnClick = true,
  ...props
}: IInputSearchWithDropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const elementClickedIsOutsideContainer =
        containerRef?.current &&
        !containerRef?.current?.contains(event?.target as Node);

      if (elementClickedIsOutsideContainer) setIsDropdownOpen(false);
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  return (
    <InputSearchWithDropdownContainer
      ref={containerRef}
      isDropdownOpen={isDropdownOpen}
      {...props}
    >
      <button onClick={toggleDropdown} id="toggle-dropdown-button">
        <span>
          {label} {required && <span className="required-flag">*</span>}
        </span>

        {(valueComponent ?? defaultValueComponent) && (
          <div className="value-area">
            {valueComponent ?? defaultValueComponent}
          </div>
        )}

        <DropdownArrowIcon />
      </button>

      <InputSearchWithDropdownDropdownContainer>
        <div className="search-input-container">
          <ComponentErrorBoundary>
            <SearchInput {...searchInputParams} withSearchAndXIcon />
          </ComponentErrorBoundary>
        </div>

        <div
          className="content"
          onClick={
            shouldCloseOnClick ? () => setIsDropdownOpen(false) : () => null
          }
        >
          {props?.children}
        </div>
      </InputSearchWithDropdownDropdownContainer>
    </InputSearchWithDropdownContainer>
  );
};
